import { Bell, ChevronLeft, PackageSearch } from "lucide-react";
import { ReactNode } from "react";
import { goBack, goToFallback, navigate, ViewName } from "./router";

export function MobileShell({
  children,
  className = "",
  bottomNav = false,
}: {
  children: ReactNode;
  className?: string;
  bottomNav?: boolean;
}) {
  return (
    <main
      className={`mobile-app app-screen ${bottomNav ? "has-bottom-nav" : ""} ${className}`}
    >
      {children}
      {bottomNav && <BottomNav />}
    </main>
  );
}

export function TopNav({
  title,
  fallback = "home",
  right,
  tone = "dark",
  forceFallback = false,
}: {
  title?: string;
  fallback?: ViewName;
  right?: ReactNode;
  tone?: "dark" | "light";
  forceFallback?: boolean;
}) {
  return (
    <header className={`top-nav top-nav--${tone}`}>
      <button
        className="icon-button"
        type="button"
        onClick={() => forceFallback ? goToFallback(fallback) : goBack(fallback)}
        aria-label="뒤로가기"
      >
        <ChevronLeft size={24} strokeWidth={1.7} />
      </button>
      {title && <h1>{title}</h1>}
      <div className="top-nav__right">{right}</div>
    </header>
  );
}

type BottomNavIconName = "home" | "ai" | "shop" | "my";

const navItems: Array<{
  view: ViewName;
  label: string;
  icon: BottomNavIconName;
}> = [
  { view: "home", label: "홈", icon: "home" },
  { view: "packaging", label: "AI 패키징", icon: "ai" },
  { view: "productHome", label: "쇼핑", icon: "shop" },
  { view: "my", label: "마이", icon: "my" },
];

function BottomNavIcon({
  name,
  active,
}: {
  name: BottomNavIconName;
  active: boolean;
}) {
  if (name === "home" && active) {
    return (
      <span className="nav-glyph nav-glyph--home" aria-hidden="true">
        <img
          className="nav-home-active-body"
          src="/assets/nav-home-fill.svg"
          alt=""
        />
        <img
          className="nav-home-active-line"
          src="/assets/nav-home-line.svg"
          alt=""
        />
      </span>
    );
  }

  if (name === "shop" && active) {
    return (
      <span className="nav-glyph nav-glyph--shop" aria-hidden="true">
        <img
          className="nav-shop-active-body"
          src="/assets/nav-shop-active-body.svg"
          alt=""
        />
        <img
          className="nav-shop-active-top"
          src="/assets/nav-shop-active-top.svg"
          alt=""
        />
        <img
          className="nav-shop-active-handle"
          src="/assets/nav-shop-active-handle.svg"
          alt=""
        />
      </span>
    );
  }

  const layers =
    name === "home"
      ? ["nav-layer--home-body", "nav-layer--home-line"]
      : name === "ai"
        ? ["nav-layer--ai-circle", "nav-layer--ai-a", "nav-layer--ai-i"]
        : name === "shop"
          ? [
              "nav-layer--shop-body",
              "nav-layer--shop-top",
              "nav-layer--shop-handle",
            ]
          : ["nav-layer--my-circle", "nav-layer--my-face"];

  return (
    <span className={`nav-glyph nav-glyph--${name}`} aria-hidden="true">
      {layers.map((layer) => (
        <i className={`nav-layer ${layer}`} key={layer} />
      ))}
    </span>
  );
}

export function BottomNav() {
  const active =
    new URL(window.location.href).searchParams.get("view") || "home";
  return (
    <nav className="bottom-nav" aria-label="주요 메뉴">
      {navItems.map(({ view, label, icon }) => {
        const isActive = active === view;
        return (
          <button
            key={view}
            type="button"
            className={isActive ? "is-active" : ""}
            onClick={() => navigate(view)}
          >
            <BottomNavIcon name={icon} active={isActive} />
            <span>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export function HomeHeader({ light = false }: { light?: boolean }) {
  return (
    <div className={`home-header ${light ? "is-light" : ""}`}>
      <img
        className="home-header__logo"
        src={light ? "/assets/sodamwon-logo-official-light.png" : "/assets/sodamwon-logo-official.png"}
        alt="소담원"
      />
      <button
        type="button"
        onClick={() => navigate("notices")}
        aria-label="공지사항"
      >
        <Bell size={20} />
      </button>
    </div>
  );
}

export function FixedButton({
  children,
  onClick,
  disabled = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="fixed-action">
      <button type="button" disabled={disabled} onClick={onClick}>
        {children}
      </button>
    </div>
  );
}

export function AppDialog({
  title,
  description,
  confirmText = "확인",
  cancelText,
  onConfirm,
  onCancel,
}: {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}) {
  return (
    <div className="app-dialog-backdrop" role="presentation">
      <section
        className="app-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
      >
        <h2 id="app-dialog-title">{title}</h2>
        {description && <p>{description}</p>}
        <div className="app-dialog__actions">
          {cancelText && (
            <button type="button" className="is-secondary" onClick={onCancel}>
              {cancelText}
            </button>
          )}
          <button type="button" autoFocus onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </section>
    </div>
  );
}

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="empty-state">
      <PackageSearch size={38} strokeWidth={1.4} />
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}
